import { Input, InputField, InputSlot } from "../../../../../ui/input";
import { Text } from "../../../../../ui/text";
import {
  digitsOnly,
  formatMoneyDigits,
  normalizeDecimalInput,
} from "./moneyDigits";

/** Opts a box into capped, possibly decimal input. Without it: whole units, grouped. */
export type AmountPrecision = {
  /** 0 keeps the box to whole units; more allows one separator and that many decimals. */
  decimals: number;
  maxIntegerDigits: number;
};

type Props = {
  /** The raw amount, e.g. "1500000" or "49.90". */
  value: string;
  currency: string;
  onChange: (amount: string) => void;
  onBlur?: () => void;
  disabled?: boolean;
  invalid?: boolean;
  accessibilityLabel: string;
  precision?: AmountPrecision;
};

/**
 * A money amount box: the brand's currency shown (never chosen) in a slot and anything
 * that is not part of an amount ignored.
 *
 * By default whole units, grouped by the currency's locale. With `precision` the text is
 * kept as typed (normalised to a `.` decimal separator and capped) — grouping a number
 * under the cursor while its decimals are being typed would fight the person.
 */
const AmountInput = ({
  value,
  currency,
  onChange,
  onBlur,
  disabled,
  invalid,
  accessibilityLabel,
  precision,
}: Props) => {
  const decimal = precision && precision.decimals > 0;
  return (
    <Input size="md" isDisabled={disabled} isInvalid={invalid}>
      {currency ? (
        <InputSlot className="pl-3">
          <Text className="text-sm text-typography-500">{currency}</Text>
        </InputSlot>
      ) : null}
      <InputField
        placeholder="0"
        keyboardType={decimal ? "decimal-pad" : "numeric"}
        value={decimal ? value : formatMoneyDigits(value, currency)}
        onChangeText={(raw) =>
          onChange(
            precision
              ? normalizeDecimalInput(
                  raw,
                  precision.decimals,
                  precision.maxIntegerDigits,
                )
              : digitsOnly(raw),
          )
        }
        onBlur={onBlur}
        accessibilityLabel={accessibilityLabel}
      />
    </Input>
  );
};

export default AmountInput;
